// SettingsCommon.js - Shared imports and utilities for Settings Configuration
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Switch,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExtensionIcon from '@mui/icons-material/Extension';

// API utilities
import { authFetch, authFetchRequest } from '../utils/api';

// Re-export all components for use by tab components
export {
  React,
  useEffect,
  useState,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Switch,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  DataGrid,
  DeleteIcon,
  EditIcon,
  SettingsIcon,
  PersonAddIcon,
  ExtensionIcon,
  authFetch,
  authFetchRequest
};

// Fallback available pages (used if API fails)
// The real list is fetched from /user-roles/available-pages
export const FALLBACK_PAGES = [
  { id: 'home', label: 'Home', path: '/' },
  { id: 'quotes', label: 'Quotes', path: '/Quotes' },
  { id: 'customers', label: 'Customers', path: '/Customers' },
  { id: 'orders', label: 'Orders', path: '/Orders' },
  { id: 'production', label: 'Production', path: '/Production' },
  { id: 'warehouse', label: 'Warehouse', path: '/Warehouse' },
  { id: 'setup', label: 'Setup', path: '/Setup' },
  { id: 'statistics', label: 'Statistics', path: '/Statistics' },
  { id: 'reports', label: 'Reports', path: '/Reports' },
  { id: 'templates', label: 'Templates', path: '/Templates' },
  { id: 'settings', label: 'Settings', path: '/Settings' },
];

// For backward compatibility - components should use useAvailablePages hook instead
export const AVAILABLE_PAGES = FALLBACK_PAGES;

// Hook to fetch available features from API (for role assignment dialogs)
export const useAvailableFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    authFetch('/user-roles/available-features')
      .then(data => { setFeatures(data || []); setLoading(false); })
      .catch(err => {
        console.error('Failed to fetch available features:', err);
        setFeatures([]);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return { features, loading, error };
};

// Hook to fetch available pages from API
export const useAvailablePages = () => {
  const [pages, setPages] = useState(FALLBACK_PAGES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    authFetch('/user-roles/available-pages')
      .then(data => {
        setPages(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch available pages, using fallback:', err);
        setPages(FALLBACK_PAGES);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return { pages, loading, error };
};
